'use client';

import { useDroppable } from '@dnd-kit/core';
import { VList, VListHandle } from 'virtua';
import { cn } from '@/lib/utils';
import { ReactNode, useRef, useCallback } from 'react';
import { DraggableItem } from './draggable-item';

// Legacy interface for backwards compatibility
interface LegacyVirtualizedColumnProps {
  id: string;
  items: Array<{ key: string; content: ReactNode }>;
  className?: string;
  emptyMessage?: string;
  placeholder?: ReactNode;
}

// New lazy rendering interface
interface LazyVirtualizedColumnProps<T> {
  id: string;
  data: T[];
  getItemKey: (item: T) => string;
  renderItem: (item: T, index: number) => ReactNode;
  className?: string;
  emptyMessage?: string;
  placeholder?: ReactNode;
}

// Type guard to check if props are lazy
function isLazyProps<T>(
  props: LegacyVirtualizedColumnProps | LazyVirtualizedColumnProps<T>
): props is LazyVirtualizedColumnProps<T> {
  return 'data' in props && 'renderItem' in props;
}

export function VirtualizedColumn<T = unknown>(
  props: LegacyVirtualizedColumnProps | LazyVirtualizedColumnProps<T>
) {
  const { id, className, emptyMessage = "No items", placeholder } = props;
  const { setNodeRef, isOver } = useDroppable({ id });
  const vlistRef = useRef<VListHandle>(null);

  // Determine if we have items
  const itemCount = isLazyProps(props) ? props.data.length : props.items.length;

  // Empty state - show placeholder if provided, otherwise default message
  if (itemCount === 0) {
    return (
      <div
        ref={setNodeRef}
        className={cn(
          "h-full p-2",
          placeholder ? "" : "flex items-center justify-center",
          "transition-colors duration-200",
          isOver ? "bg-muted/50" : "",
          className
        )}
      >
        {placeholder || <p className="text-muted-foreground text-xs">{emptyMessage}</p>}
      </div>
    );
  }

  // Render using lazy approach
  if (isLazyProps(props)) {
    const { data, getItemKey, renderItem } = props;
    return (
      <div
        ref={setNodeRef}
        className={cn(
          "h-full transition-colors duration-200",
          isOver ? "bg-muted/50" : "",
          className
        )}
      >
        <VList
          ref={vlistRef}
          className="h-full"
          style={{ padding: '4px', paddingBottom: '8px' }}
        >
          {data.map((item, index) => {
            const key = getItemKey(item);
            return (
              <DraggableItem key={key} id={key} index={index} data={{ category: id }}>
                {renderItem(item, index)}
              </DraggableItem>
            );
          })}
        </VList>
      </div>
    );
  }

  // Legacy render path (pre-created items)
  const { items } = props;
  return (
    <div
      ref={setNodeRef}
      className={cn(
        "h-full transition-colors duration-200",
        isOver ? "bg-muted/50" : "",
        className
      )}
    >
      <VList
        ref={vlistRef}
        className="h-full"
        style={{ padding: '4px', paddingBottom: '8px' }}
      >
        {items.map((item, index) => (
          <DraggableItem key={item.key} id={item.key} index={index} data={{ category: id }}>
            {item.content}
          </DraggableItem>
        ))}
      </VList>
    </div>
  );
}
